import {
  schAlert,
  schAloftDate,
  schAloftPrice,
  schLocationType,
  schPropertyInterest,
  schSixTierQuality,
  schValueType,
  schZoningCompliance,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { schReconciliationSummarizedCostData } from '../../redux/features/selectors/selectReconciliationData.ts';
import { ESCALATION_TYPE_MESSAGES, STOP_AND_CALL_VALUES } from './consts.ts';

/**
 *  Validation schema for Appraisify validation
 *  This schema is used purely for validation purposes, not for UI forms
 */
export const schAppraisifyValidation = z.object({
  // Alert validation
  alerts: z.object({
    alerts: z
      .array(schAlert)
      .refine(
        (alerts) =>
          alerts.every((alert) => alert.status === 'dismissed' || alert.severity === 'warning'),
        {
          message: 'There are active alerts that need to be addressed',
        },
      ),
  }),

  // Order Details validation
  orderDetails: z
    .object({
      zoning: z.string().min(1, 'Required'),
      zoningCompliance: schZoningCompliance,
      parcelNumber: z.string().optional(),
      newConstruction: z.boolean().default(false),
      legalDescription: z.string().min(1, 'Required'),
      locationType: schLocationType,
      hoa: z.string().min(1, 'Required'),
      effectiveDate: schAloftDate,
      intendedUsers: z.string().min(1, 'Required'),
      intendedUse: z.string().min(1, 'Required'),
      propertyInterest: schPropertyInterest,
      propertyInterestOther: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if ((!data.parcelNumber || data.parcelNumber.trim().length === 0) && !data.newConstruction) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['parcelNumber'],
        });
      }

      if (data.propertyInterest === 'Other') {
        if (!data.propertyInterestOther || data.propertyInterestOther.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['propertyInterestOther'],
          });
        }
      }
    }),

  // Market validation
  market: z.object({}),

  // Analysis validation
  analysis: z.object({
    opinionOfSiteValue: schAloftPrice.refine(
      (price) => price.toDollarLike() > 0,
      'Opinion of site value must be greater than 0',
    ),
  }),

  // Commentary validation
  commentary: z.object({
    estimatedDemand: z.string().min(1, 'Required'),
    estimatedExposureTime: z.string().min(1, 'Required'),
    marketConditionCommentary: z.string().min(1, 'Required'),
    priorSalesCommentary: z.string().min(1, 'Required'),
  }),

  // Reconciliation validation
  reconciliation: z
    .object({
      totalCostToCure: schAloftPrice.optional(),
      totalCostToCureCommentary: z.string().min(1, 'Required'),
      opinionOfMarketValue: schAloftPrice,
      opinionOfAfterRepairValue: schAloftPrice.optional(),
      totalRenovationBudget: schAloftPrice.optional(),
      includeTotalRenovationBudget: z.boolean(),
      includeOpinionOfAfterRepairValue: z.boolean(),
      includeTotalCostToCure: z.boolean(),
      hasArvOption: z.boolean(),
      reconciliationCommentary: z.string().min(1, 'Required'),
      highestAndBestUseCommentary: z.string().min(1, 'Required'),
      revisionCommentary: z.string().min(1, 'Required'),
      alternativeComparablesCommentary: z.array(
        z.string().min(1, 'Each alternative comparable must be addressed with a commentary'),
      ),
      feasibilityCommentary: z.string().min(1, 'Required'),
      aivDiscrepancyCommentary: z.string().optional(),
      arvDiscrepancyCommentary: z.string().optional(),
      hasAivDiscrepancy: z.boolean(),
      hasArvDiscrepancy: z.boolean(),
      isMockOrder: z.boolean(),
      valueTypes: z.array(schValueType).optional(),
      reconciliationData: z.object({
        general: schReconciliationSummarizedCostData,
        aiv: schReconciliationSummarizedCostData,
        arv: schReconciliationSummarizedCostData,
      }),
    })
    .superRefine((data, ctx) => {
      if (!data.isMockOrder && (!data.valueTypes || data.valueTypes.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a value type option',
          path: ['valueTypes'],
        });
      }

      if (data.hasAivDiscrepancy && !data.aivDiscrepancyCommentary?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['aivDiscrepancyCommentary'],
        });
      }

      if (data.hasArvDiscrepancy && !data.arvDiscrepancyCommentary?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['arvDiscrepancyCommentary'],
        });
      }

      if (data.hasArvOption) {
        if (!data.totalCostToCure) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['totalCostToCure'],
          });
        }

        if (!data.opinionOfAfterRepairValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['opinionOfAfterRepairValue'],
          });
        }

        if (!data.totalRenovationBudget) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['totalRenovationBudget'],
          });
        }

        // if (
        //   data.reconciliationData.aiv.maxCompMarketAdjustedSalePrice &&
        //   data.opinionOfMarketValue.gt(data.reconciliationData.aiv.maxCompMarketAdjustedSalePrice)
        // ) {
        //   ctx.addIssue({
        //     code: z.ZodIssueCode.custom,
        //     message: `Opinion of As-Is Value cannot be greater than the maximum market adjusted sale price of the comps (${data.reconciliationData.aiv.maxCompMarketAdjustedSalePrice.toString(true)}).`,
        //     path: ['opinionOfMarketValue'],
        //   });
        // }

        // if (
        //   data.opinionOfAfterRepairValue &&
        //   data.reconciliationData.arv.maxCompMarketAdjustedSalePrice &&
        //   data.opinionOfAfterRepairValue.gt(
        //     data.reconciliationData.arv.maxCompMarketAdjustedSalePrice,
        //   )
        // ) {
        //   ctx.addIssue({
        //     code: z.ZodIssueCode.custom,
        //     message: `Opinion of After-Repair Value cannot be greater than the maximum market adjusted sale price of the comps (${data.reconciliationData.arv.maxCompMarketAdjustedSalePrice.toString(true)}).`,
        //     path: ['opinionOfAfterRepairValue'],
        //   });
        // }

        // if (
        //   data.reconciliationData.aiv.minCompMarketAdjustedSalePrice &&
        //   data.opinionOfMarketValue.lt(data.reconciliationData.aiv.minCompMarketAdjustedSalePrice)
        // ) {
        //   ctx.addIssue({
        //     code: z.ZodIssueCode.custom,
        //     message: `Opinion of As-Is Value cannot be less than the minimum market adjusted sale price of the comps (${data.reconciliationData.aiv.minCompMarketAdjustedSalePrice.toString(true)}).`,
        //     path: ['opinionOfMarketValue'],
        //   });
        // }
        // if (
        //   data.opinionOfAfterRepairValue &&
        //   data.reconciliationData.arv.minCompMarketAdjustedSalePrice &&
        //   data.opinionOfAfterRepairValue.lt(
        //     data.reconciliationData.arv.minCompMarketAdjustedSalePrice,
        //   )
        // ) {
        //   ctx.addIssue({
        //     code: z.ZodIssueCode.custom,
        //     message: `Opinion of After-Repair Value cannot be less than the minimum market adjusted sale price of the comps (${data.reconciliationData.arv.minCompMarketAdjustedSalePrice.toString(true)}).`,
        //     path: ['opinionOfAfterRepairValue'],
        //   });
        // }
      } else {
        // if (
        //   data.reconciliationData.general.maxCompMarketAdjustedSalePrice &&
        //   data.opinionOfMarketValue.gt(
        //     data.reconciliationData.general.maxCompMarketAdjustedSalePrice,
        //   )
        // ) {
        //   ctx.addIssue({
        //     code: z.ZodIssueCode.custom,
        //     message: `Opinion of Market Value cannot be greater than the maximum market adjusted sale price of the comps (${data.reconciliationData.general.maxCompMarketAdjustedSalePrice.toString(true)}).`,
        //     path: ['opinionOfMarketValue'],
        //   });
        // }
        // if (
        //   data.reconciliationData.general.minCompMarketAdjustedSalePrice &&
        //   data.opinionOfMarketValue.lt(
        //     data.reconciliationData.general.minCompMarketAdjustedSalePrice,
        //   )
        // ) {
        //   ctx.addIssue({
        //     code: z.ZodIssueCode.custom,
        //     message: `Opinion of Market Value cannot be less than the minimum market adjusted sale price of the comps (${data.reconciliationData.general.minCompMarketAdjustedSalePrice.toString(true)}).`,
        //     path: ['opinionOfMarketValue'],
        //   });
        // }
      }
    }),
});

export type AppraisifyValidationData = z.infer<typeof schAppraisifyValidation>;

export const schAppraisifyStopAndCall = z.object({
  orderDetails: z
    .object({
      zoningCompliance: schZoningCompliance,
      propertyInterest: schPropertyInterest,
    })
    .partial()
    .superRefine((data, ctx) => {
      if (
        data.propertyInterest &&
        STOP_AND_CALL_VALUES.propertyInterest.includes(data.propertyInterest)
      ) {
        let message = '';
        if (data.propertyInterest === 'Leased Fee') {
          message = ESCALATION_TYPE_MESSAGES.leased_fee_property_interest;
        } else if (data.propertyInterest === 'Leasehold') {
          message = ESCALATION_TYPE_MESSAGES.leasehold_property_interest;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['propertyInterest'],
        });
      }
      if (
        data.zoningCompliance &&
        STOP_AND_CALL_VALUES.zoningCompliance.includes(data.zoningCompliance)
      ) {
        let message = '';
        if (data.zoningCompliance === 'Illegal') {
          message = ESCALATION_TYPE_MESSAGES.zoning_compliance_illegal;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['zoningCompliance'],
        });
      }
    }),
  features: z.object({
    subject: z
      .object({
        uadCondition: schSixTierQuality,
      })
      .partial()
      .superRefine((data, ctx) => {
        if (data.uadCondition && STOP_AND_CALL_VALUES.uadCondition.includes(data.uadCondition)) {
          let message = '';

          if (data.uadCondition === 5) {
            message = ESCALATION_TYPE_MESSAGES.c5_condition;
          } else if (data.uadCondition === 6) {
            message = ESCALATION_TYPE_MESSAGES.c6_condition;
          }

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['uadCondition'],
          });
        }
      }),
  }),
  reconciliation: z
    .object({
      valueTypes: z.array(schValueType),
      allowSubjectToValue: z.boolean(),
    })
    .partial()
    .superRefine((data, ctx) => {
      if (
        data.valueTypes &&
        !data.allowSubjectToValue &&
        data.valueTypes.some((type) => STOP_AND_CALL_VALUES.valueTypes.includes(type))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ESCALATION_TYPE_MESSAGES.value_subject_to,
          path: ['valueTypes'],
        });
      }
    }),
});

export type AppraisifyStopAndCallData = z.infer<typeof schAppraisifyStopAndCall>;
